import { FormikHelpers } from 'formik';
import { observable, action } from 'mobx';
import history from 'helpers/routerHistory';
import { scrollToTop } from 'helpers/helpers';
import { ACCOUNTS_EDIT_PLAN } from 'pages/Accounts/Edit/accountEditRoutes';
import { RiskProfiles, QuestionnaireQuestions, SingleValueResponse } from 'common/interfaces';
import {
  API_ACCOUNT_VARIANT,
  API_ACCOUNT_EDIT_QUESTIONNAIRE,
  API_ACCOUNT_QUESTIONNAIRE_RESULT,
  API_ACCOUNT_EDIT_STOP,
  API_ACCOUNT_QUESTIONNAIRE_CHECK,
  API_ACCOUNT_DETAIL_VARIANT,
  API_ACCOUNT_DETAIL_EDIT_QUESTIONNAIRE,
  API_ACCOUNT_DETAIL_QUESTIONNAIRE_RESULT,
  API_ACCOUNT_DETAIL_EDIT_STOP,
  API_ACCOUNT_DETAIL_PLAN,
  API_ACCOUNT_DETAIL_QUESTIONNAIRE_CHECK,
  API_ACCOUNT_QUESTIONNAIRE_REASON,
  API_ACCOUNT_DETAIL_QUESTIONNAIRE_REASON,
  API_ACCOUNT_QUESTIONNAIRE_SKIP,
} from 'api/endpoints';
import { FormStore } from '../../formStore';
import { RootStore } from 'stores/rootStore';
import { AccountMode } from 'enums';

interface Variant {
  mode: AccountMode;
  portfolioOnly: boolean;
  currentVersion: string;
  storedVersion?: string;
  planRendered?: boolean;
}

export class WorkflowQuestionnaireStore extends FormStore<QuestionnaireQuestions> {
  @observable showResult = true;
  @observable resultData: RiskProfiles | undefined = undefined;
  @observable completePlan: boolean | undefined = undefined;

  @observable inconsistencyInfo = false;
  @observable inconsistencies: { key: string; questions: string[] }[] | null = null;
  @observable inQuestions: {
    [key: string]: { previous: string[]; change?: string; inconsistency?: string; reasons?: { [key: string]: string } };
  } | null = null;
  @observable previousAnswers?: { [key: string]: string | string[] } = undefined;
  @observable changesDialog = false;
  @observable changes: { key: string; previous: string | string[]; current: string | string[] }[] | null = null;
  @observable answers = true;

  @observable version = '';

  @observable tab = 0;

  @observable profileChange = 0;

  preferEsg = false;
  planRendered: boolean | undefined = false;

  detailMode = false;
  userMode = false;
  endpoints = {
    VARIANT: API_ACCOUNT_VARIANT,
    EDIT: API_ACCOUNT_EDIT_QUESTIONNAIRE,
    RESULT: API_ACCOUNT_QUESTIONNAIRE_RESULT,
    STOP: API_ACCOUNT_EDIT_STOP,
    CHECK: API_ACCOUNT_QUESTIONNAIRE_CHECK,
    REASON: API_ACCOUNT_QUESTIONNAIRE_REASON,
  };

  accountId = '';
  @action
  setAccountId = (accountId: string) => (this.accountId = accountId);

  setPreferEsg = () => (this.preferEsg = true);

  constructor(rootStore: RootStore, accountId: string, detailMode?: boolean, userMode?: boolean) {
    super(rootStore);
    this.accountId = accountId;
    this.detailMode = detailMode || false;
    this.userMode = userMode || false;
    if (detailMode) {
      this.endpoints = {
        VARIANT: API_ACCOUNT_DETAIL_VARIANT,
        EDIT: API_ACCOUNT_DETAIL_EDIT_QUESTIONNAIRE,
        RESULT: API_ACCOUNT_DETAIL_QUESTIONNAIRE_RESULT,
        STOP: API_ACCOUNT_DETAIL_EDIT_STOP,
        CHECK: API_ACCOUNT_DETAIL_QUESTIONNAIRE_CHECK,
        REASON: API_ACCOUNT_DETAIL_QUESTIONNAIRE_REASON,
      };
    }
  }

  @action
  setTab = (t: number) => (this.tab = t);

  handleNextStep = () => {
    history.push(ACCOUNTS_EDIT_PLAN(this.accountId));
  };

  clearPlanCache = () => {
    this.rootStore.stores.apiStore.removeFromCache(API_ACCOUNT_DETAIL_PLAN(this.accountId));
  };

  @action handleTakeAgain = async () => {
    this.resetData();
    this.answers = true;
    await this.fetchQuestionnaire();
    this.setTab(0);
    scrollToTop();
  };

  @action fetchQuestionnaireData = async (questionnaire?: boolean) => {
    questionnaire ? await this.fetchQuestionnaire() : await this.fetchResult();
  };

  @action
  fetchQuestionnaire = async () => {
    this.rootStore.stores.uiStore.setLoading(true);
    const response = await this.rootStore.stores.apiStore.get<Variant>(this.endpoints.VARIANT(this.accountId));
    //let storedVersion = null;
    if (response?.data) {
      this.completePlan = response.data.portfolioOnly === false;
      this.planRendered = response.data.planRendered;
      this.version = response.data.currentVersion;
      //storedVersion = response.data.storedVersion;
    }
    /*
    if (!storedVersion || storedVersion === this.version) {
      const response = await this.rootStore.stores.apiStore.get<{ answers: QuestionnaireQuestions }>(
        this.endpoints.EDIT(this.accountId),
      );
      if (response?.data?.answers) {
        this.data = response.data.answers;
        this.previousAnswers = response.data.answers;
      }
    } else {
      const response = await this.rootStore.stores.apiStore.get<{ answers: QuestionnaireQuestions }>(
        this.endpoints.EDIT(this.accountId),
      );
      if (response?.data?.answers) {
        this.data = response.data.answers;
      }
    }
    */

    const response2 = await this.rootStore.stores.apiStore.get<{ answers: QuestionnaireQuestions }>(
      this.endpoints.EDIT(this.accountId),
    );
    if (response2?.data?.answers) {
      this.data = response2.data.answers;
      this.previousAnswers = response2.data.answers;
    }

    this.rootStore.stores.uiStore.setLoading(false);
    this.showResult = false;
    this.restoreInQuesitons();
    this.restorePrevValues();
  };

  @action
  async fetchResult() {
    this.rootStore.stores.uiStore.setLoading(true);
    const response = await this.rootStore.stores.apiStore.get<RiskProfiles>(this.endpoints.RESULT(this.accountId));
    if (response) {
      if (Object.keys(response?.data || {}).length && !response.data?.result.skipped) {
        this.resultData = response.data;
        this.showResult = true;
        this.profileChange = response.data?.result.profileChange || 0;
      } else {
        this.fetchQuestionnaire();
        this.showResult = false;
      }
    }
    this.rootStore.stores.uiStore.setLoading(false);
  }

  @action
  advance = () => {
    this.setTab(this.tab + 1);
    scrollToTop();
  };

  @action
  check = async (values: QuestionnaireQuestions) => {
    this.data = values;
    const changes = this.detailMode || this.planRendered ? this.checkChanges() : false;
    if (!changes) {
      await this.checkInconsistencies();
    }
  };

  @action
  checkChanges = () => {
    const values = this.data!;
    if ((this.detailMode || this.planRendered) && this.previousAnswers) {
      this.changes = [];
      Object.keys(values).forEach((key) => {
        if (
          this.previousAnswers![key] &&
          values[key] &&
          this.previousAnswers![key].toString() !== values[key].toString() &&
          (!this.inQuestions ||
            !this.inQuestions[key] ||
            (this.inQuestions[key].change && !this.inQuestions[key].inconsistency))
        ) {
          this.changes!.push({ key, current: values[key], previous: this.previousAnswers![key] });
        }
      });
    }
    if (this.changes?.length) {
      if (!this.inQuestions) this.inQuestions = {};
      this.changes.forEach((c) => {
        const previous = Array.isArray(c.previous) ? (c.previous as string[]) : [c.previous as string];
        if (!this.inQuestions![c.key]) {
          this.inQuestions![c.key] = { previous, change: '', reasons: { 0: '' } };
        } else {
          if (!this.inQuestions![c.key].reasons) {
            this.inQuestions![c.key].reasons = { 0: '' };
          } else {
            this.inQuestions![c.key].reasons![0] = '';
          }
          this.inQuestions![c.key].change = '';
        }
      });
      this.answers = false;
      this.backupInQuestions();
      this.changesDialog = true;
      return true;
    } else {
      return false;
    }
  };

  @action
  updatePreviousAnswers = () => {
    this.previousAnswers = this.data!;
    this.backupPrevValues();
  };

  @action
  checkInconsistencies = async () => {
    const values = this.data!;
    const response = await this.rootStore.stores.apiStore.post<{ key: string; questions: string[] }[]>(
      this.endpoints.CHECK(this.accountId),
      { answers: values },
    );
    if (response?.data) {
      if (response.data.length === 0) {
        this.advance();
      } else {
        this.inconsistencies = response.data;

        if (!this.inQuestions) this.inQuestions = {};

        this.inconsistencies.forEach((i) => {
          i.questions.forEach((q) => {
            if (this.inQuestions![q]) {
              if (!this.inQuestions![q].reasons) {
                this.inQuestions![q].reasons = { [i.key]: '' };
              } else {
                this.inQuestions![q].reasons![i.key] = '';
              }
              this.inQuestions![q].inconsistency = '';
            } else {
              this.inQuestions![q] = {
                previous: Array.isArray(values[q]) ? (values[q] as string[]) : [values[q] as string],
                reasons: { [i.key]: '' },
                inconsistency: '',
              };
            }
          });
        });
        this.answers = false;
        this.backupInQuestions();
        this.inconsistencyInfo = true;
      }
    }
  };

  @action
  updateReason = (inconsistency: string, questions: string[], reason: string) => {
    if (this.inQuestions !== null) {
      Object.keys(this.inQuestions).forEach((key) => {
        if (this.inQuestions![key].reasons && this.inQuestions![key].reasons![inconsistency] !== undefined) {
          this.inQuestions![key].reasons![inconsistency] = reason;
        }
      });
      questions.forEach((q) => {
        this.inQuestions![q].inconsistency = reason;
      });
      this.backupInQuestions();
      this.answers = this.answersFilledIn();
    }
  };

  @action
  updateChange = (question: string, reason: string) => {
    if (this.inQuestions !== null) {
      if (this.inQuestions![question].reasons) {
        this.inQuestions![question].reasons!['0'] = reason;
      }
      this.inQuestions![question].change = reason;
      this.backupInQuestions();
      this.answers = this.answersFilledIn();
    }
  };

  answersFilledIn = () => {
    return (
      this.inQuestions === null ||
      Object.values(this.inQuestions).reduce((prev, current) => {
        prev += current.reasons ? Object.values(current.reasons).filter((r) => r.length < 5).length : 0;
        return prev;
      }, 0) === 0
    );
  };

  backupInQuestions = () => {
    sessionStorage.setItem(`IQ.${this.accountId}`, JSON.stringify(this.inQuestions));
  };

  backupPrevValues = () => {
    sessionStorage.setItem(`IQ.${this.accountId}.previous`, JSON.stringify(this.previousAnswers));
  };

  restorePrevValues = () => {
    const backup = sessionStorage.getItem(`IQ.${this.accountId}.previous`);
    if (backup) this.previousAnswers = JSON.parse(backup);
  };

  restoreInQuesitons = () => {
    const backup = sessionStorage.getItem(`IQ.${this.accountId}`);
    this.inQuestions = backup ? JSON.parse(backup) : null;
  };

  resetInQuestions = () => {
    this.inQuestions = null;
    sessionStorage.removeItem(`IQ.${this.accountId}`);
  };

  resetPrevValues = () => {
    this.previousAnswers = undefined;
    sessionStorage.removeItem(`IQ.${this.accountId}.previous`);
  };

  @action
  hideInconsistencyInfo = () => (this.inconsistencyInfo = false);

  @action
  hideChangesDialog = () => {
    this.changesDialog = false;
  };

  skipQuestionnaire = async () => {
    const response = await this.rootStore.stores.apiStore.post<SingleValueResponse<boolean>>(
      API_ACCOUNT_QUESTIONNAIRE_SKIP(this.accountId),
    );
    if (response?.data?.value) {
      history.push(ACCOUNTS_EDIT_PLAN(this.accountId));
    }
  };

  handleSubmitQuestionnaire = async (
    values: QuestionnaireQuestions,
    formikHelpers: FormikHelpers<QuestionnaireQuestions>,
  ) => {
    const questionsSaved = await this.submitData(
      this.endpoints.EDIT(this.accountId, this.preferEsg ? '?preferredEsg=true' : ''),
      { answers: values },
      {
        formikHelpers,
      },
    );
    let reasonsResolved = true;
    if (questionsSaved && this.inQuestions !== null) {
      const reasons: { [key: string]: { previous: string[]; reason: string } } = {};
      Object.keys(values).forEach((key) => {
        const current = Array.isArray(values[key]) ? values[key] : [values[key]];
        if (
          this.inQuestions![key] &&
          values[key] !== '' &&
          values[key] !== [] &&
          current.toString() !== this.inQuestions![key].previous.toString()
        ) {
          reasons[key] = {
            previous: this.inQuestions![key].previous,
            reason: this.inQuestions![key].inconsistency || this.inQuestions![key].change || '',
          };
        }
      });
      const response = await this.rootStore.stores.apiStore.post<SingleValueResponse<boolean>>(
        this.endpoints.REASON(this.accountId),
        { reasons },
      );
      if (response?.data?.value !== true) {
        reasonsResolved = false;
      }
    }
    if (questionsSaved && reasonsResolved) {
      this.resetInQuestions();
      this.resetPrevValues();
      this.clearPlanCache();
      await this.fetchResult();
    }
  };

  async stopWorkflow(questionNumber: string) {
    const { apiStore } = this.rootStore.stores;

    const response = await apiStore.post<{ data: SingleValueResponse<boolean> }>(
      this.endpoints.STOP(this.accountId),
      questionNumber,
    );
    return !!response;
  }
}
